import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaClock, FaArrowRight } from 'react-icons/fa';
import { blogPosts, featuredPosts } from './blogData';

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 140px);
  background: #ffffff;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  color: white;
  padding: 3rem 5%;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', 'Times New Roman', serif;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  span { color: #ffd966; }
`;

const HeroLine = styled.div`
  width: 60px; height: 2px; background: #ffd966; margin: 0.8rem auto;
  border-radius: 2px;
`;

const HeroDescription = styled.p`
  font-size: 1rem; opacity: 0.85; max-width: 600px; margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1100px; margin: 0 auto; padding: 2.5rem 5% 3rem;
`;

const SectionTitle = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 1.8rem; color: #1a1a2e; font-weight: 800; margin-bottom: 1.5rem;
  display: flex; align-items: center; gap: 0.8rem;
  .title-accent { width: 4px; height: 26px; background: #3730A3; border-radius: 2px; }
`;

const FeaturedGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; margin-bottom: 3rem;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FeaturedCard = styled(Link)`
  text-decoration: none; background: white; border-radius: 14px; overflow: hidden;
  box-shadow: 0 4px 15px rgba(55,48,163,0.06); border: 1px solid #f0f0f5;
  transition: all 0.3s ease;
  &:hover { transform: translateY(-5px); box-shadow: 0 12px 30px rgba(55,48,163,0.1); }
`;

const FeaturedImage = styled.div`
  height: 180px; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease; }
  ${FeaturedCard}:hover & img { transform: scale(1.05); }
`;

const FeaturedContent = styled.div` padding: 1.3rem; `;

const FeaturedCategory = styled.span`
  background: #f5f3ff; color: #3730A3; padding: 0.25rem 0.8rem; border-radius: 20px;
  font-size: 0.75rem; font-weight: 700; display: inline-block; margin-bottom: 0.6rem;
`;

const FeaturedTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  font-size: 1.05rem; color: #1a1a2e; margin-bottom: 0.5rem; line-height: 1.4;
`;

const FeaturedExcerpt = styled.p`
  font-family: 'Times New Roman', serif;
  color: #4a4a6a; font-size: 0.85rem; line-height: 1.5; margin-bottom: 0.8rem;
`;

const BlogGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const BlogCard = styled(Link)`
  text-decoration: none; background: white; border-radius: 12px; overflow: hidden;
  box-shadow: 0 2px 10px rgba(55,48,163,0.04); border: 1px solid #f0f0f5;
  transition: all 0.3s ease; display: flex; flex-direction: column;
  &:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(55,48,163,0.08); }
`;

const BlogImage = styled.div`
  height: 160px; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease; }
  ${BlogCard}:hover & img { transform: scale(1.05); }
`;

const BlogContent = styled.div` padding: 1.2rem; flex: 1; display: flex; flex-direction: column; `;

const BlogCategory = styled.span`
  color: #3730A3; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.5px; margin-bottom: 0.4rem;
`;

const BlogTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  font-size: 1rem; color: #1a1a2e; margin-bottom: 0.4rem; line-height: 1.4; font-weight: 700;
`;

const BlogExcerpt = styled.p`
  font-family: 'Times New Roman', serif;
  color: #4a4a6a; font-size: 0.85rem; line-height: 1.5; margin-bottom: 1rem; flex: 1;
`;

const BlogFooter = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-top: auto;
  font-family: 'Times New Roman', serif; color: #888; font-size: 0.8rem;
`;

const ReadMore = styled.span`
  color: #3730A3; font-size: 0.8rem; font-weight: 600; display: flex; align-items: center; gap: 0.3rem;
  ${BlogCard}:hover & svg { transform: translateX(3px); }
  svg { transition: transform 0.2s ease; }
`;

const Blog = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Learn Thru Us <span>Blog</span></HeroTitle>
        <HeroLine />
        <HeroDescription>Insights, career guidance, and industry updates in medical coding and CDI</HeroDescription>
      </HeroSection>

      <Container>
        {featuredPosts.length > 0 && (
          <>
            <SectionTitle><span className="title-accent"></span>Featured Articles</SectionTitle>
            <FeaturedGrid>
              {featuredPosts.map(post => (
                <FeaturedCard key={post.id} to={`/blog/${post.slug}`}>
                  <FeaturedImage><img src={post.image} alt={post.title} /></FeaturedImage>
                  <FeaturedContent>
                    <FeaturedCategory>{post.category}</FeaturedCategory>
                    <FeaturedTitle>{post.title}</FeaturedTitle>
                    <FeaturedExcerpt>{post.excerpt.substring(0, 90)}...</FeaturedExcerpt>
                    <BlogFooter><span><FaClock /> {post.readTime}</span></BlogFooter>
                  </FeaturedContent>
                </FeaturedCard>
              ))}
            </FeaturedGrid>
          </>
        )}

        <SectionTitle><span className="title-accent"></span>All Articles</SectionTitle>
        <BlogGrid>
          {blogPosts.map(post => (
            <BlogCard key={post.id} to={`/blog/${post.slug}`}>
              <BlogImage><img src={post.image} alt={post.title} /></BlogImage>
              <BlogContent>
                <BlogCategory>{post.category}</BlogCategory>
                <BlogTitle>{post.title}</BlogTitle>
                <BlogExcerpt>{post.excerpt.substring(0, 80)}...</BlogExcerpt>
                <BlogFooter>
                  <span><FaClock /> {post.readTime}</span>
                  <ReadMore>Read More <FaArrowRight /></ReadMore>
                </BlogFooter>
              </BlogContent>
            </BlogCard>
          ))}
        </BlogGrid>
      </Container>
    </PageContainer>
  );
};

export default Blog;